import { BusinessProfile, BusinessScope } from '@/app/shared/interface/account.interface';
import { FileType, FileTypeValue } from '@/app/shared/interface/file.interface';
import { District, Province, ProvinceV2, Ward } from '@/app/shared/interface/province.interface';
import { User } from '@/app/shared/interface/user.interface';
import { AccountService } from '@/app/shared/services/account.service';
import { CommonService } from '@/app/shared/services/common.service';
import { FileService } from '@/app/shared/services/file.service';
import { ProvinceService } from '@/app/shared/services/province.service';
import { useSafeTimeout } from '@/app/shared/ssr-util';
import { Component, TransferState, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { SsrCookieService } from 'ngx-cookie-service-ssr';
import { NgxDropzoneChangeEvent } from 'ngx-dropzone';
import { ToastrService } from 'ngx-toastr';
import { Subject, firstValueFrom, takeUntil } from 'rxjs';

@Component({
  selector: 'app-business-profile',
  templateUrl: './business-profile.component.html',
  styleUrl: './business-profile.component.scss',
})
export class BusinessProfileComponent {
  @ViewChild('businessRegistrationCertificateModal', { static: false }) businessRegistrationCertificateModal: any;
  @ViewChild('businessRegistrationCertificateModal2', { static: false }) businessRegistrationCertificateModal2: any;
  @ViewChild('pharmaceuticalStandardModal', { static: false }) pharmaceuticalStandardModal: any;
  @ViewChild('confirmationModal', { static: false }) confirmationModal: any;

  public provinceV2: ProvinceV2;
  public provinceCode: number = 0;
  public districtCode: number = 0;
  public provinces: Province[] = [];
  public provincesV2: ProvinceV2[] = [];
  public districts: District[] = [];
  public wards: Ward[] = [];
  public selectedProvince: number;
  public selectedDistrict: number;
  public selectedWard: number;
  public addressDetail: string;
  public selectedBusinessType: number;
  public fileType: string = FileTypeValue.UserDocument;
  public user: User = new User();
  public businessProfile: BusinessProfile = new BusinessProfile();
  public businessLicenses: string[] = [];
  public businessLicensesImage: string[] | null;
  public businessLicensesType: string;
  public businessCertifications: string[] = [];
  public businessCertificationsImage: string[] | null;
  public businessCertificationsType: string;
  public distributionCertifications: string[] = [];
  public distributionCertificationsImage: string[] | null;
  public distributionCertificationsType: string;
  public budgetRelationShipCode: string = '';
  public readonly budgetRelationShipCodeMaxLength: number = 7;
  public buyerName: string = '';
  public allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'application/pdf'];
  public fileSizeLimit = 10;
  public formOfBusinesses = [
    {
      code: 0,
      name: 'Nhà thuốc',
    },
    {
      code: 1,
      name: 'Quầy thuốc',
    },
    {
      code: 2,
      name: 'Tủ thuốc trạm y tế xã',
    },
    {
      code: 3,
      name: 'Cơ sở bán buôn',
    },
    {
      code: 4,
      name: 'Bệnh viện',
    },
    {
      code: 5,
      name: 'Cơ sở khám chữa bệnh khác',
    },
    {
      code: 6,
      name: 'Khác',
    },
  ];
  form: FormGroup;
  formBackup: any = {};

  select2Provinces: any;
  select2Districts: any;
  select2Wards: any;

  select2ProvinceV2: any;
  select2WardsV2: any;

  loadingDistricts: boolean = false;
  loadingWards: boolean = false;

  businessScopes: any[] = [];
  businessScopesBackup: any[] = [];

  isExpiredDateWarning = false;
  hasUserConfirmedExpiredSubmit = false;

  isAddressV2: boolean = false;
  isAddressV2Backup: boolean = false;

  destroy$ = new Subject<void>();

  private safeTimeout = useSafeTimeout();

  constructor(
    private toastService: ToastrService,
    private commonService: CommonService,
    private formBuilder: FormBuilder,
    private accountService: AccountService,
    private fileService: FileService,
    private provinceService: ProvinceService,
    private transferState: TransferState,
    private cookieService: SsrCookieService,
  ) {
    this.form = this.formBuilder.group({
      businessType: new FormControl({ value: '', disabled: true }, [Validators.required]),
      businessName: new FormControl({ value: '', disabled: true }, [Validators.required]),
      taxCode: new FormControl({ value: '', disabled: true }),
      province: new FormControl({ value: null, disabled: true }, [Validators.required]),
      district: new FormControl({ value: null, disabled: true }),
      ward: new FormControl({ value: null, disabled: true }, [Validators.required]),
      address: new FormControl({ value: '', disabled: true }),
      businessLicenses: new FormControl(null),
      businessLicensesNo: new FormControl({ value: '', disabled: true }),
      businessLicensesDate: new FormControl({ value: '', disabled: true }),
      businessLicensesProvince: new FormControl({ value: '', disabled: true }),
      businessCertifications: new FormControl(null),
      businessCertificationsNo: new FormControl({ value: '', disabled: true }),
      businessCertificationsDate: new FormControl({ value: '', disabled: true }),
      businessCertificationsProvince: new FormControl({ value: '', disabled: true }),
      distributionCertifications: new FormControl(null),
      distributionCertificationsNo: new FormControl({ value: '', disabled: true }),
      distributionCertificationsDate: new FormControl({ value: '', disabled: true }),
      distributionCertificationsExpirationDate: new FormControl({ value: '', disabled: true }),
      distributionCertificationsProvince: new FormControl({ value: '', disabled: true }),
      budgetRelationShipCode: new FormControl({ value: '', disabled: false }),
      buyerName: new FormControl({ value: '', disabled: false }, [Validators.required]),
      documentData: new FormControl(null),
    });
  }

  get budgetRelationShipCodeRemaining(): number {
    const length = this.form.controls['budgetRelationShipCode'].value?.length ?? 0;
    return Math.max(this.budgetRelationShipCodeMaxLength - length, 0);
  }

  ngOnInit() {
    this.businessScopes = Object.keys(BusinessScope)
      .filter((key) => isNaN(Number(key))) // removes "0" to "13"
      .map((key) => ({
        code: BusinessScope[key as keyof typeof BusinessScope],
        name: this.getScopeName(BusinessScope[key as keyof typeof BusinessScope]),
      }));

    this.getBusinessProfile();
    // this.provinceService
    //   .getProvinceInfoByRegion()
    //   .subscribe((res: any) => {
    //     if (res) {
    //       this.provinces = res;
    //       this.select2Provinces = this.provinces.map(
    //         (province: Province) =>
    //           new Object({
    //             value: province.code,
    //             label: province.name,
    //           })
    //       );
    //       this.getBusinessProfile();
    //     }
    //   });
  }

  getBusinessProfile() {
    //get business profile
    this.accountService.getUserData.pipe(takeUntil(this.destroy$)).subscribe(async (res) => {
      if (res && res.businessName) {
        this.user = res;
        if (!this.user.businessRegistrationCertData) {
          this.user.businessRegistrationCertData = {
            documentNo: '',
            issuedDate: '',
            issuedPlace: '',
          };
        }
        if (!this.user.businessRegistrationCert2Data) {
          this.user.businessRegistrationCert2Data = {
            documentNo: '',
            issuedDate: '',
            issuedPlace: '',
          };
        }
        if (!this.user.pharmaceuticalStandardData) {
          this.user.pharmaceuticalStandardData = {
            documentNo: '',
            issuedDate: '',
            issuedPlace: '',
            expireDate: '',
            businessScopes: [],
          };
        }

        this.selectedBusinessType = res.businessType!;
        this.budgetRelationShipCode = res.budgetRelationShipCode ?? '';
        this.buyerName = res.buyerName ?? '';
        this.addressDetail = res.address?.address ?? '';
        if (res.address?.provinceV2) {
          this.selectedProvince = res.address.provinceV2.code;
          this.selectedWard = res.address.provinceV2.wards[0]?.code ?? 0;

          this.provinceService.getProvinceV2InfoByRegion().subscribe({
            next: (res: ProvinceV2[]) => {
              this.provincesV2 = res ?? [];
              this.select2ProvinceV2 = this.provincesV2.map(
                (province: ProvinceV2) =>
                  new Object({
                    value: province.code,
                    label: province.name,
                  }),
              );
            },
            error: (err: any) => {
              console.error(err);
            },
          });

          this.provinceService.getProvinceV2InfoByDepth(this.selectedProvince, this.selectedWard).subscribe({
            next: (res: ProvinceV2[]) => {
              if (res && res.length) {
                this.wards = res[0].wards;
                this.select2WardsV2 = res[0].wards
                  ? res[0].wards.map(
                      (ward: Ward) =>
                        new Object({
                          value: ward.code,
                          label: ward.name,
                        }),
                    )
                  : [];
              }
            },
            error: (err: any) => {
              console.error(err);
            },
            complete: () => {},
          });

          this.isAddressV2 = true;
          this.isAddressV2Backup = true;
        } else if (res.address?.province) {
          this.isAddressV2 = false;
          this.provinceService.getProvinceInfoByRegion().subscribe((res: any) => {
            if (res) {
              this.provinces = res;
              this.select2Provinces = this.provinces.map(
                (province: Province) =>
                  new Object({
                    value: province.code,
                    label: province.name,
                  }),
              );
            }
          });
          this.selectedProvince = res.address?.province.code!;
          //district

          const resDistrict = await firstValueFrom(
            this.provinceService.getProvinceInfoByDepth(this.selectedProvince, this.districtCode),
          );
          if (resDistrict) {
            this.districts = resDistrict[0].districts;
            this.select2Districts = this.districts.map(
              (district: District) =>
                new Object({
                  value: district.code,
                  label: district.name,
                }),
            );
            this.selectedDistrict = res.address?.province.districts ? res.address?.province.districts[0].code : 0;
            //ward

            const resWard = await firstValueFrom(
              this.provinceService.getProvinceInfoByDepth(this.selectedProvince, this.selectedDistrict),
            );

            if (resWard) {
              this.select2Wards = resWard[0].districts[0].wards
                ? resWard[0].districts[0].wards.map(
                    (ward: Ward) =>
                      new Object({
                        value: ward.code,
                        label: ward.name,
                      }),
                  )
                : [];
              this.selectedWard = res.address?.province.districts
                ? res.address?.province.districts[0].wards[0].code
                : 0;
            }
          }
        }

        this.loadDocumentImage(
          res.businessRegistrationCert,
          res.businessRegistrationCertPending,
          (val) => (this.businessLicensesImage = val),
        );
        this.loadDocumentImage(
          res.businessRegistrationCert2,
          res.businessRegistrationCert2Pending,
          (val) => (this.businessCertificationsImage = val),
        );
        this.loadDocumentImage(
          res.pharmaceuticalStandard,
          res.pharmaceuticalStandardPending,
          (val) => (this.distributionCertificationsImage = val),
        );

        if (res.pharmaceuticalStandardData && res.pharmaceuticalStandardData.businessScopes) {
          this.user.pharmaceuticalStandardData.businessScopes = res.pharmaceuticalStandardData.businessScopes;
          this.businessScopes = this.businessScopes.map((scope) => {
            return {
              ...scope,
              checked: res.pharmaceuticalStandardData.businessScopes?.some((x) => x == scope.code) ? true : false,
            };
          });
          this.businessScopesBackup = [...this.businessScopes];
        }

        this.form.patchValue({
          businessType: res.businessType,
          businessName: res.businessName,
          taxCode: res.taxNumber,
          province: res.address?.provinceV2?.code ?? res.address?.province?.code,
          district: this.selectedDistrict,
          ward: this.selectedWard,
          address: res.address?.address,
          businessLicensesNo: res.businessRegistrationCertData.documentNo,
          businessLicensesDate: this.commonService.convertDateToNgbDate(res.businessRegistrationCertData.issuedDate),
          businessLicensesProvince: res.businessRegistrationCertData.issuedPlace,
          businessCertificationsNo: res.businessRegistrationCert2Data.documentNo,
          businessCertificationsDate: this.commonService.convertDateToNgbDate(
            res.businessRegistrationCert2Data.issuedDate,
          ),
          businessCertificationsProvince: res.businessRegistrationCert2Data.issuedPlace,
          distributionCertificationsNo: res.pharmaceuticalStandardData.documentNo,
          distributionCertificationsDate: this.commonService.convertDateToNgbDate(
            res.pharmaceuticalStandardData.issuedDate,
          ),
          distributionCertificationsExpirationDate: this.commonService.convertDateToNgbDate(
            res.pharmaceuticalStandardData.expireDate,
          ),
          distributionCertificationsProvince: res.pharmaceuticalStandardData.issuedPlace,
          budgetRelationShipCode: res.budgetRelationShipCode,
          buyerName: res.buyerName,
        });

        for (let key in this.form.controls) {
          this.formBackup[key] = this.form.controls[key].value;
        }

        if (this.commonService.topbarContent) {
          this.commonService.topbarContent.visible = false;
        }
      }
    });
  }

  remove(type: number) {
    switch (type) {
      case 0:
        this.businessLicensesImage = null;
        this.businessProfile.businessLicenses = [];
        break;
      case 1:
        this.businessCertificationsImage = null;
        this.businessProfile.businessCertifications = [];
        break;
      case 2:
        this.distributionCertificationsImage = null;
        this.businessProfile.distributionCertifications = [];
        break;
    }
  }

  getFileInfo(files: string[]) {
    let content = files[0].substring(files[0].lastIndexOf('/')).slice(1);
    let type = files[0].substring(files[0].lastIndexOf('.')).slice(1);
    if (type.includes('pdf')) {
      type = 'application/' + type;
    } else {
      type = 'image/' + type;
    }
    let data = new Blob([content], {
      type: type + files[0].substring(files[0].lastIndexOf('.')).slice(1),
    });
    let arrayOfBlob = new Array<Blob>();
    arrayOfBlob.push(data);
    let applicationZip = new File(arrayOfBlob, content, {
      type: type,
    });
    console.log(applicationZip);
    return applicationZip;
  }

  onSelect(event: NgxDropzoneChangeEvent, type: number) {
    if (event.addedFiles.length > 0) {
      switch (type) {
        case 0:
          if (this.checkFileType(event)) {
            this.businessProfile.businessLicenses = event.addedFiles;
            // this.setFiles(this.businessProfile.businessLicenses, event);
            if (!event.addedFiles[0].type.includes('pdf')) {
              this.businessRegistrationCertificateModal.open();
            }
          }
          break;
        case 1:
          if (this.checkFileType(event)) {
            this.businessProfile.businessCertifications = event.addedFiles;
            // this.setFiles(this.businessProfile.businessCertifications, event);
            if (!event.addedFiles[0].type.includes('pdf')) {
              this.businessRegistrationCertificateModal2.open();
            }
          }
          break;
        case 2:
          if (this.checkFileType(event)) {
            this.businessProfile.distributionCertifications = event.addedFiles;
            // this.setFiles(this.businessProfile.distributionCertifications, event);
            if (!event.addedFiles[0].type.includes('pdf')) {
              this.pharmaceuticalStandardModal.open();
            }
          }
          break;
      }
    }
  }

  checkFileType(event: any) {
    if (event.addedFiles.length === 0) {
      this.toastService.error('Vui lòng tải lên file ảnh hoặc pdf', '', {
        positionClass: 'toast-top-right',
      });
      return false;
    }
    const file = event.addedFiles[0];
    if (!this.allowedTypes.includes(file.type)) {
      this.toastService.error('Chỉ được up file ảnh hoặc pdf', '', {
        positionClass: 'toast-top-right',
      });
      return false;
    }
    const fileSizeInMB = file.size / (1024 * 1024);
    if (fileSizeInMB > this.fileSizeLimit) {
      this.toastService.error('File không được quá 10MB', '', {
        positionClass: 'toast-top-right',
      });
      return false;
    }
    return true;
  }

  onRemove(event: File, type: number) {
    switch (type) {
      case 0:
        this.businessProfile.businessLicenses.splice(this.businessProfile.businessLicenses.indexOf(event), 1);
        if (this.isAddressV2Backup) {
          this.selectedProvince = this.user?.address?.provinceV2?.code ?? 0;
          this.selectedWard = this.user?.address?.provinceV2?.wards[0]?.code ?? 0;
          this.patchValueForm(this.formBackup, type);
          this.isAddressV2 = true;
        } else {
          this.isAddressV2 = false;
          console.log(this.form.get('ward')?.value);
          console.log(this.formBackup.ward);

          this.loadDistrictsByProvince({ value: this.formBackup.province }, () => {
            this.loadWardsByDistrict({ value: this.formBackup.district }, () => {
              this.safeTimeout(() => {
                this.patchValueForm(this.formBackup, type);
              }, 150);
            });
          });
        }

        break;
      case 1:
        this.businessProfile.businessCertifications.splice(
          this.businessProfile.businessCertifications.indexOf(event),
          1,
        );
        this.patchValueForm(this.formBackup, type);
        break;
      case 2:
        this.businessProfile.distributionCertifications.splice(
          this.businessProfile.distributionCertifications.indexOf(event),
          1,
        );
        this.patchValueForm({ form: this.formBackup, scopes: this.businessScopesBackup }, type);
        break;
    }
  }

  setFiles(files: File[], event: any) {
    if (files.length + event.addedFiles.length < 2) {
      files.push(...event.addedFiles);
    } else {
      files = event.addedFiles;
      // this.toastService.error(`Bạn chỉ được up 1 file.`, '', {
      //   positionClass: 'toast-top-right',
      // });
    }
  }

  async loadDistrictsByProvince($event: any, callback?: () => void) {
    //filter district by province
    if ($event.value && !this.loadingDistricts && this.selectedProvince != $event.value) {
      this.loadingDistricts = true;

      const res = await firstValueFrom(
        this.provinceService.getProvinceInfoByDepth(
          $event.value,
          0, // 0 means get all districts of the province
        ),
      );

      if (res) {
        this.districts = res[0].districts;
        this.select2Districts = this.districts.map(
          (district: District) =>
            new Object({
              value: district.code,
              label: district.name,
            }),
        );
        this.selectedProvince = $event.value;
        this.selectedDistrict = 0;
        this.selectedWard = 0;
        this.form.controls['province'].setValue(this.selectedProvince);
        this.form.get('district')?.reset();
        this.form.get('ward')?.reset();
        this.select2Wards = [];
        this.wards = [];
        this.loadingDistricts = false;

        this.form.patchValue({
          province: $event.value,
        });

        if (callback) {
          callback();
        }
      }
    }
  }

  async loadWardsByDistrict($event: any, callback?: () => void) {
    if ($event.value && !this.loadingWards && this.selectedDistrict != $event.value) {
      this.loadingWards = true;

      const res = await firstValueFrom(
        this.provinceService.getProvinceInfoByDepth(
          this.form.controls['province'].value || this.selectedProvince,
          $event.value,
        ),
      );

      if (res && res[0].districts.length > 0 && res[0].districts[0]) {
        this.wards = res[0].districts[0].wards;
        this.select2Wards = this.wards.map(
          (ward: Ward) =>
            new Object({
              value: ward.code,
              label: ward.name,
            }),
        );
      }
      this.loadingWards = false;
      this.form.patchValue({
        district: $event.value,
      });

      if (callback) {
        callback();
      }
    }
  }

  async loadUrlFileUpload(files: File[], type: Number) {
    if (files.length > 0) {
      const token = this.cookieService.get('token');
      // const token = this.cookieService.get('token');
      let jwtToken = this.commonService.parseJwt(token!);
      // console.log(jwtToken);
      // console.log(files);
      // console.log(this.fileType);
      await this.fileService
        .uploadFileUser(files, this.fileType, jwtToken.UserName)
        .toPromise()
        .then((res: any) => {
          console.log(res);
          if (res) {
            if (type == 1) {
              this.businessLicenses.push(res.fileUrl);
            }
            if (type == 2) {
              this.businessCertifications.push(res.fileUrl);
            }
            if (type == 3) {
              this.distributionCertifications.push(res.fileUrl);
            }
          }
        });
    }
  }

  async getWardAndProvince(province: number, ward: number) {
    let res = await firstValueFrom(this.provinceService.getProvinceV2InfoByDepth(province, ward));
    if (res && res.length) {
      this.selectedProvince = province;
      this.select2ProvinceV2 = [
        new Object({
          value: res[0].code,
          label: res[0].name,
        }),
      ];
      this.selectedWard = ward;
      this.select2WardsV2 = res[0].wards.map(
        (ward: Ward) =>
          new Object({
            value: ward.code,
            label: ward.name,
          }),
      );
    }
  }

  async save() {
    this.form.markAllAsTouched();
    if (!this.form.valid) {
      if (this.form.controls['province'].hasError('required')) {
        this.toastService.error('Chưa chọn Tỉnh/Thành phố!', '', { positionClass: 'toast-top-right' });
      }
      if (this.form.controls['ward'].hasError('required')) {
        this.toastService.error('Chưa chọn Phường/Xã!', '', { positionClass: 'toast-top-right' });
      }
      if (this.form.controls['budgetRelationShipCode'].hasError('maxlength')) {
        this.toastService.error(`Mã quan hệ ngân sách tối đa ${this.budgetRelationShipCodeMaxLength} ký tự!`, '', {
          positionClass: 'toast-top-right',
        });
      }
      if (this.form.controls['buyerName'].hasError('required')) {
        this.toastService.error('Vui lòng nhập họ tên người mua hàng!', '', { positionClass: 'toast-top-right' });
        return;
      }
      return; // Dừng hàm nếu form không hợp lệ
    }

    if (!this.form.valid) {
      this.toastService.error('Xin vui lòng điền đầy đủ thông tin!', '', { positionClass: 'toast-top-right' });
      return;
    }

    this.user.businessType = Number(this.form.controls['businessType'].value);
    this.user.businessName = this.form.controls['businessName'].value;
    this.user.budgetRelationShipCode = this.form.controls['budgetRelationShipCode'].value;
    this.user.buyerName = this.form.controls['buyerName'].value;

    var res = await firstValueFrom(
      this.provinceService.getProvinceV2InfoByDepth(this.form.get('province')?.value, this.form.get('ward')?.value),
    );

    if (res && res.length) {
      this.provinceV2 = res[0];
    } else {
      this.toastService.error('Có lỗi xảy ra, vui lòng thử lại sau!', '', { positionClass: 'toast-top-right' });
      return;
    }

    this.user.address!.provinceV2 = this.provinceV2;
    this.user.address!.address = this.form.controls['address'].value;
    this.user.taxNumber = this.form.controls['taxCode'].value;

    if (this.businessProfile.businessLicenses.length > 0) {
      await this.loadUrlFileUpload(this.businessProfile.businessLicenses, 1);
      if (this.businessLicenses != null && this.businessLicenses.length > 0) {
        this.user.businessRegistrationCert = this.businessLicenses;
      }
    } else if (this.businessLicensesImage == null) {
      this.user.businessRegistrationCert = [];
    }

    if (this.businessProfile.businessCertifications.length > 0) {
      await this.loadUrlFileUpload(this.businessProfile.businessCertifications, 2);
      if (this.businessCertifications != null) {
        this.user.businessRegistrationCert2 = this.businessCertifications;
      }
    } else if (this.businessCertificationsImage == null) {
      this.user.businessRegistrationCert2 = [];
    }

    if (this.businessProfile.distributionCertifications.length > 0) {
      await this.loadUrlFileUpload(this.businessProfile.distributionCertifications, 3);
      if (this.distributionCertifications != null) {
        this.user.pharmaceuticalStandard = this.distributionCertifications;
      }
    } else if (this.distributionCertificationsImage == null) {
      this.user.pharmaceuticalStandard = [];
    }

    if (this.user.businessRegistrationCert.length == 0) {
      this.toastService.error('Vui lòng tải lên Giấy phép đăng ký kinh doanh!', '', {
        positionClass: 'toast-top-right',
      });
      return;
    }

    // if (this.user.businessRegistrationCert2.length == 0) {
    //   this.toastService.error('Vui lòng tải lên Giấy chứng nhận đủ điều kiện kinh doanh dược!', '', {
    //     positionClass: 'toast-top-right',
    //   });
    //   return;
    // }

    // if (this.user.pharmaceuticalStandard.length == 0) {
    //   this.toastService.error('Vui lòng tải lên Giấy chứng nhận đạt thực hành tốt phân phối thuốc, nguyên liệu làm thuốc (GPP)!', '', {
    //     positionClass: 'toast-top-right',
    //   });
    //   return;
    // }

    this.confirmationModal.openModal();
  }

  selectScope(scope: any) {
    if (this.user.pharmaceuticalStandardData.businessScopes?.some((x) => x == scope)) {
      this.user.pharmaceuticalStandardData.businessScopes = this.user.pharmaceuticalStandardData.businessScopes.filter(
        (x) => x != scope,
      );
    } else {
      this.user.pharmaceuticalStandardData.businessScopes?.push(scope);
    }
  }

  getScopeName(scope: BusinessScope) {
    switch (scope) {
      case BusinessScope.GeneralProduct:
        return 'TBYT, TPCN, Dược Mỹ Phẩm, Nhóm hàng tiêu dùng';
      case BusinessScope.RegularNonPrescription:
        return 'Thuốc thường không kê đơn';
      case BusinessScope.RegularPrescription:
        return 'Thuốc thường kê đơn';
      case BusinessScope.EssentialPrescriptionDrug:
        return 'Thuốc kê đơn thiết yếu';
      case BusinessScope.NarcoticCombinationPrescription:
        return 'Thuốc phối hợp chứa dược chất gây nghiện kê đơn';
      case BusinessScope.NarcoticCombinationNonPrescription:
        return 'Thuốc phối hợp chứa dược chất gây nghiện không kê đơn';
      case BusinessScope.PsychotropicCombinationPrescription:
        return 'Thuốc phối hợp chứa dược chất hướng thần kê đơn';
      case BusinessScope.PsychotropicCombinationNonPrescription:
        return 'Thuốc phối hợp chứa dược chất hướng thần không kê đơn';
      case BusinessScope.PrecursorCombinationPrescription:
        return 'Thuốc phối hợp chứa tiền chất kê đơn';
      case BusinessScope.PrecursorCombinationNonPrescription:
        return 'Thuốc phối hợp chứa tiền chất không kê đơn';
      case BusinessScope.ProhibitedInIndustriesPrescription:
        return 'Thuốc cấm sử dụng trong 1 số ngành kê đơn';
      case BusinessScope.ProhibitedInIndustriesNonPrescription:
        return 'Thuốc cấm sử dụng trong 1 số ngành không kê đơn';
      case BusinessScope.EssentialProhibitedInIndustriesPrescription:
        return 'Thuốc cấm sử dụng trong 1 số ngành kê đơn thiết yếu';
      case BusinessScope.BiologicProduct:
        return 'Sinh phẩm';
      default:
        return 'Không xác định';
    }
  }

  async onConfirmForm(event: any, type: number) {
    if (type === 0) {
      this.isAddressV2 = true;
      await this.getWardAndProvince(event.province, event.ward);
    }
    this.safeTimeout(() => {
      this.patchValueForm(event, type);
    }, 150);
  }

  onCloseForm(type: number) {
    this.remove(type);
    switch (type) {
      case 0:
      case 1:
        this.patchValueForm(this.formBackup, type);
        break;
      case 2:
        this.patchValueForm({ form: this.formBackup, scopes: this.businessScopesBackup }, type);
        break;
    }
  }

  checkExpirationDate(val: any) {
    const expireDate = this.commonService.convertNgbDateToDate(val);
    if (expireDate) {
      const nowUtc = new Date();
      const expire = new Date(expireDate);
      this.isExpiredDateWarning = expire.getTime() <= nowUtc.getTime();
    } else {
      this.isExpiredDateWarning = false;
    }
  }

  patchValueForm(data: any, type: number) {
    switch (type) {
      case 0:
        this.form.patchValue({
          businessName: data.businessName,
          taxCode: data.taxCode,
          province: data.province,
          district: data.district,
          ward: data.ward,
          address: data.address,
          businessLicensesNo: data.businessLicensesNo,
          businessLicensesDate: data.businessLicensesDate,
          businessLicensesProvince: data.businessLicensesProvince,
          budgetRelationShipCode: this.budgetRelationShipCode,
          buyerName: data.buyerName,
        });
        break;
      case 1:
        this.form.patchValue({
          businessCertificationsNo: data.businessCertificationsNo,
          businessCertificationsDate: data.businessCertificationsDate,
          businessCertificationsProvince: data.businessCertificationsProvince,
        });
        break;
      case 2:
        this.form.patchValue({
          distributionCertificationsNo: data.form.distributionCertificationsNo,
          distributionCertificationsDate: data.form.distributionCertificationsDate,
          distributionCertificationsProvince: data.form.distributionCertificationsProvince,
          distributionCertificationsExpirationDate: data.form.distributionCertificationsExpirationDate,
        });
        this.businessScopes = data.scopes;
        break;
    }
  }

  onConfirm(event: any) {
    if (event) {
      this.user.businessRegistrationCertData.documentNo = this.form.controls['businessLicensesNo'].value;
      this.user.businessRegistrationCertData.issuedDate = this.commonService.convertNgbDateToDate(
        this.form.controls['businessLicensesDate'].value,
      );
      this.user.businessRegistrationCertData.issuedPlace = this.form.controls['businessLicensesProvince'].value;
      this.user.businessRegistrationCert2Data.documentNo = this.form.controls['businessCertificationsNo'].value;
      this.user.businessRegistrationCert2Data.issuedDate = this.commonService.convertNgbDateToDate(
        this.form.controls['businessCertificationsDate'].value,
      );
      this.user.businessRegistrationCert2Data.issuedPlace = this.form.controls['businessCertificationsProvince'].value;
      this.user.pharmaceuticalStandardData.documentNo = this.form.controls['distributionCertificationsNo'].value;
      this.user.pharmaceuticalStandardData.issuedDate = this.commonService.convertNgbDateToDate(
        this.form.controls['distributionCertificationsDate'].value,
      );
      this.user.pharmaceuticalStandardData.expireDate = this.commonService.convertNgbDateToDate(
        this.form.controls['distributionCertificationsExpirationDate'].value,
      );
      this.user.pharmaceuticalStandardData.issuedPlace = this.form.controls['distributionCertificationsProvince'].value;
      this.user.pharmaceuticalStandardData.businessScopes = this.businessScopes
        .filter((scope) => scope.checked)
        .map((scope) => scope.code);
      this.user.budgetRelationShipCode = this.form.controls['budgetRelationShipCode'].value;

      this.user.isReviewRequested = true;

      this.accountService.updateUser(JSON.stringify(this.user)).subscribe((res) => {
        if (res) {
          this.toastService.success('Sửa hồ sơ kinh doanh thành công', '', {
            positionClass: 'toast-top-right',
          });
          this.accountService.setUserData(this.user);
          this.getBusinessProfile();
        } else {
          this.toastService.error('Sửa hồ sơ kinh doanh thất bại', '', {
            positionClass: 'toast-top-right',
          });
        }
      });
    }
  }

  private loadDocumentImage(
    files: string[] | undefined,
    pendingFiles: string[] | undefined,
    setImage: (val: string[]) => void,
  ): void {
    const source = pendingFiles && pendingFiles.length > 0 ? pendingFiles : files;

    if (!source || source.length === 0) return;

    const cert = source[0];

    if (cert.includes('giathuochapu.com')) {
      setImage([cert]);
      return;
    }

    this.fileService.getSingedUrl(FileType.UserDocument, cert.substring(cert.lastIndexOf('/')).slice(1)).subscribe({
      next: (res: any) => {
        setImage(res);
      },
    });
  }

  ngOnDestroy() {
    if (this.commonService.topbarContent) {
      this.commonService.topbarContent.visible = true;
    }
    this.destroy$.next();
    this.destroy$.complete();
  }
}
